import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import clsx from 'clsx';
import { getApplication, getApplicationStatus, type ApplicationStatus, type NextAction } from '@/api/applicant';
import { Card } from '@/components/ui/Card';

export const appKeys = { app: ['applicant-application'] as const, status: ['applicant-status'] as const, exam: ['applicant-exam'] as const, result: ['applicant-exam-result'] as const };

export const useApplication = () => useQuery({ queryKey: appKeys.app, queryFn: getApplication });
export const useStatus = () => useQuery({ queryKey: appKeys.status, queryFn: getApplicationStatus });

export const newKey = () => (globalThis.crypto?.randomUUID ? globalThis.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

export const STATUS_LABEL: Record<ApplicationStatus, string> = {
  draft: 'Draft — not submitted', submitted: 'Submitted — under review', payment_pending: 'Awaiting form fee payment', paid: 'Form fee paid — under review',
  exam_scheduled: 'Entrance exam scheduled', exam_taken: 'Exam taken — awaiting decision', admitted: 'Admission offered', waitlisted: 'On the waiting list',
  rejected: 'Not admitted', accepted: 'Offer accepted', declined: 'Offer declined', enrolled: 'Admitted — you are now a student',
};

const STATUS_TONE: Partial<Record<ApplicationStatus, string>> = {
  admitted: 'bg-green-100 text-green-800', accepted: 'bg-green-100 text-green-800', enrolled: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800', declined: 'bg-red-100 text-red-800', waitlisted: 'bg-purple-100 text-purple-800',
  payment_pending: 'bg-amber-100 text-amber-800', exam_scheduled: 'bg-blue-100 text-blue-800',
};

export function AppStatusBadge({ status }: { status: ApplicationStatus | null | undefined }) {
  if (!status) return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">Not started</span>;
  return <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium', STATUS_TONE[status] ?? 'bg-slate-100 text-slate-700')}>{STATUS_LABEL[status] ?? status}</span>;
}

/** Where the applicant should go next, by `next_action`. */
export const NEXT_STEP: Record<NextAction, { to: string | null; label: string; text: string }> = {
  complete_form: { to: '/applicant/application', label: 'Complete application', text: 'Fill in the applicant and guardian details.' },
  submit: { to: '/applicant/application', label: 'Review & submit', text: 'Your form is complete. Upload documents and submit it.' },
  pay: { to: '/applicant/payment', label: 'Pay form fee', text: 'Pay the application form fee so the school can process your application.' },
  wait: { to: null, label: '', text: 'The school is reviewing your application. We will notify you of the next step.' },
  take_exam: { to: '/applicant/exam', label: 'Go to entrance exam', text: 'Your entrance exam is ready.' },
  respond_offer: { to: '/applicant/offer', label: 'Respond to offer', text: 'You have an admission offer. Accept or decline it before it expires.' },
  upload_document: { to: '/applicant/documents', label: 'Upload document', text: 'The school asked for another document. Upload it so they can continue.' },
  none: { to: null, label: '', text: 'There is nothing you need to do right now.' },
};

export function Steps({ steps }: { steps: { key: string; label: string; done: boolean }[] }) {
  if (!steps.length) return null;
  const current = steps.findIndex((s) => !s.done);
  return (
    <ol className="space-y-3">
      {steps.map((s, i) => (
        <li key={s.key} className="flex items-center gap-3 text-sm">
          <span className={clsx('w-7 h-7 rounded-full grid place-items-center text-xs font-semibold shrink-0', s.done ? 'bg-green-600 text-white' : i === current ? 'bg-brand-600 text-white' : 'bg-slate-100 text-slate-500')}>
            {s.done ? <Check size={14} /> : i + 1}
          </span>
          <span className={clsx(s.done ? 'text-slate-700' : i === current ? 'font-semibold text-navy' : 'text-slate-400')}>{s.label}</span>
        </li>
      ))}
    </ol>
  );
}

/** Shown on pages that need a saved application first. */
export function NeedApplication({ children = 'Start your application first.' }: { children?: ReactNode }) {
  return (
    <Card>
      <p className="text-sm text-slate-600">{children}</p>
      <Link to="/applicant/application" className="inline-block mt-3 text-sm font-semibold text-brand-600">Go to application form</Link>
    </Card>
  );
}

export const DOC_TYPES: { value: string; label: string }[] = [
  { value: 'birth_certificate', label: 'Birth certificate' },
  { value: 'passport_photo', label: 'Passport photograph' },
  { value: 'last_result', label: 'Last result / report card' },
  { value: 'report_card', label: 'Last school report card' },
  { value: 'transfer_letter', label: 'Transfer letter' },
  { value: 'medical_report', label: 'Medical report' },
  { value: 'other', label: 'Other' },
];
export const docLabel = (t: string) => DOC_TYPES.find((d) => d.value === t)?.label ?? t.replace(/_/g, ' ');
